import { Article } from 'interfaces/homepage';

import { PaywallProps } from '@components/Paywall';
import {
  api_articles_recommendation_path,
  api_user_recommended_articles_path,
  paywall_params_api_article_path,
  share_url_api_article_path,
} from '@routes';

import request from './request';

export const ARTICLE_SHARE_KEY = 'article_share';
export const ARTICLE_PAYWALL_PROPS_KEY = 'article_paywall_props';
export const ARTICLES_PROPS_KEY = 'articles_props';

export interface ArticleShareResponse {
  canShareArticle: boolean;
  escapedArticleTitle: string;
  escapedGoLink: string;
  shareUrl: string;
}

export interface ArticlePaywallPropsResponse {
  articleId: number;
  paywallProps: PaywallProps;
}

export async function getArticleShareLink({
  articleId,
}: {
  articleId: number;
}): Promise<ArticleShareResponse> {
  const resp = await request('get', share_url_api_article_path(articleId));
  return resp;
}

export async function getArticlePaywallProps({
  articleId,
  isTwoOptPaywall,
  offerCode,
  shareGuid,
}: Pick<
  PaywallProps,
  'articleId' | 'isTwoOptPaywall' | 'offerCode' | 'shareGuid'
>): Promise<ArticlePaywallPropsResponse> {
  const params = shareGuid
    ? { offer: offerCode, share_guid: shareGuid }
    : { is_two_opt_paywall: isTwoOptPaywall, offer: offerCode };
  const resp = await request('get', paywall_params_api_article_path(articleId, params));
  return resp;
}

interface RecommendedArticlesParams {
  articleSlugs: string[];
}

interface UserRecommendedArticlesResponse {
  articles: Article[];
  popular: boolean;
}

export async function getRecommendedArticles({
  articleSlugs,
}: RecommendedArticlesParams): Promise<Article[]> {
  const response = await request(
    'get',
    api_articles_recommendation_path({ article_slugs: articleSlugs })
  );
  return response.articles;
}

export async function getUserRecommendedArticles(): Promise<UserRecommendedArticlesResponse> {
  const response = await request('get', api_user_recommended_articles_path());
  return response;
}

export interface ArticleCitationPreview {
  title: string | null;
  imageUrl: string | null;
}

// Metadata lookup for AI Deep Research inline citation hover cards: title and
// Imgix hero URL (either may be null — no picture, or lookup failure — so the
// caller can fall back to a text-only card / the chip's own title). The title
// matters while the answer is still streaming, when a chip exists before the
// citations list that normally supplies its headline. The path is built by
// hand rather than via js-routes so a new route doesn't require regenerating
// the @routes bundle.
export async function getArticleCitationPreview(
  articleId: string
): Promise<ArticleCitationPreview> {
  try {
    const response: { id: number; title?: string | null; imageUrl: string | null } = await request(
      'get',
      `/api/v1/articles/${articleId}/preview`
    );
    return { title: response?.title ?? null, imageUrl: response?.imageUrl ?? null };
  } catch {
    return { title: null, imageUrl: null };
  }
}
